import type { DisciplineType } from '../../services/api/models/DisciplineType';
import { DISCIPLINE_ORDER } from '../../constants/disciplines';
import type { AgencyClinicResponse, OrgMemberResponse } from './types';

const ALL_DISCIPLINES = DISCIPLINE_ORDER;

export type ClinicDisciplineRow = {
  clinic_id: string;
  clinic_name: string;
  disciplines: Set<DisciplineType>;
};

export function syncRowsToPendingSelection(
  pendingIds: Set<string>,
  clinics: AgencyClinicResponse[],
  member: OrgMemberResponse | null,
  prevRows: ClinicDisciplineRow[]
): ClinicDisciplineRow[] {
  const clinicNameById = new Map<string, string>();
  for (const clinic of clinics) {
    clinicNameById.set(clinic.id, clinic.name);
  }
  for (const assignment of member?.clinic_assignments ?? []) {
    // The member payload names each clinic itself, and that copy is blank for
    // an unnamed clinic. Keep the resolved name from `clinics` in that case.
    if (!assignment.clinic_name?.trim()) continue;
    clinicNameById.set(assignment.clinic_id, assignment.clinic_name);
  }

  const savedDisciplinesByClinicId = new Map<string, Set<DisciplineType>>();
  for (const assignment of member?.clinic_assignments ?? []) {
    if (assignment.is_assignable_clinician) {
      savedDisciplinesByClinicId.set(
        assignment.clinic_id,
        new Set(assignment.disciplines ?? [])
      );
    }
  }

  const prevByClinicId = new Map(prevRows.map(row => [row.clinic_id, row]));
  const visibleClinicIds = new Set(clinics.map(clinic => clinic.id));

  return [...pendingIds]
    .filter(clinicId => visibleClinicIds.has(clinicId))
    .map(clinicId => {
      const existing = prevByClinicId.get(clinicId);
      if (existing) {
        // Rows seeded from the member payload can carry a blank name; adopt
        // the resolved one without disturbing the discipline selection.
        const resolved = clinicNameById.get(clinicId);
        if (resolved && !existing.clinic_name?.trim()) {
          return { ...existing, clinic_name: resolved };
        }
        return existing;
      }

      const saved = savedDisciplinesByClinicId.get(clinicId);
      return {
        clinic_id: clinicId,
        clinic_name: clinicNameById.get(clinicId) ?? 'Unknown clinic',
        disciplines: saved ? new Set(saved) : new Set(ALL_DISCIPLINES),
      };
    })
    .sort((a, b) => a.clinic_name.localeCompare(b.clinic_name));
}
